import React, { useMemo } from "react";

import { PACKAGE_SELECTOR_PAGE_SIZE } from "constants/constants";
import usePaginatedList from "hooks/usePaginatedList";
import { getPackageLabel, PACKAGE_SEARCHABLE_FIELDS } from "utils/packageUtils";
import {
  EmptyState,
  Icon,
  Loader,
  Pagination,
  QueryErrorPanel,
  SearchBar,
} from "widgets";

import styles from "../PackageSelector/PackageSelector.module.scss";
import viewStyles from "./PackageListView.module.scss";

const PackageListView = ({
  selectAll = false,
  selectedIds = [],
  viewMode = false,
  packages = [],
  isLoading = false,
  isError = false,
  fetchError = null,
  onRefetch,
}) => {
  const displayPackages = useMemo(() => {
    if (selectAll) return packages;
    return selectedIds
      .map((id) => packages.find((pkg) => pkg.packageId === id))
      .filter(Boolean);
  }, [packages, selectAll, selectedIds]);

  const {
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    filteredItems: filteredPackages,
    paginatedItems: paginatedPackages,
    totalPages,
  } = usePaginatedList(
    displayPackages,
    PACKAGE_SELECTOR_PAGE_SIZE,
    PACKAGE_SEARCHABLE_FIELDS
  );

  if (isLoading) {
    return (
      <div className={viewStyles.statePanel}>
        <Loader variant="inline" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={viewStyles.statePanel}>
        <QueryErrorPanel
          message={fetchError?.message || "Failed to load packages."}
          onRetry={onRefetch}
        />
      </div>
    );
  }

  if (selectAll) {
    return (
      <div className={viewStyles.container}>
        <div className={styles.infoBanner}>
          <Icon name="info" fontSize="small" />
          <span>
            {viewMode
              ? `This coupon applies to all packages${
                  packages.length ? ` (${packages.length} total)` : ""
                }.`
              : `Coupon is applicable to all packages${
                  packages.length ? ` (${packages.length} total)` : ""
                }.`}
          </span>
        </div>

        <div className={viewStyles.panel}>
          <div className={styles.paneHeader}>
            <h3>{viewMode ? "Applicable Packages" : "All Packages"}</h3>
            <span>{filteredPackages.length} total</span>
          </div>

          <div className={viewStyles.toolbar}>
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              searchableFields={PACKAGE_SEARCHABLE_FIELDS}
              fieldLabels={{
                displayName: "name",
                packageName: "name",
                packageId: "ID",
              }}
            />
          </div>

          <div className={viewStyles.panelBody}>
            <PackageBoxGrid
              packages={paginatedPackages}
              emptyMessage="No packages found."
            />
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={filteredPackages.length}
            pageSize={PACKAGE_SELECTOR_PAGE_SIZE}
            onPageChange={setCurrentPage}
            itemLabel="packages"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={viewStyles.container}>
      <div className={viewStyles.panel}>
        <div className={styles.paneHeader}>
          <h3>
            {viewMode
              ? `Applicable Packages (${displayPackages.length})`
              : `Selected Packages (${displayPackages.length})`}
          </h3>
          <span>{filteredPackages.length} shown</span>
        </div>

        <div className={viewStyles.toolbar}>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            searchableFields={PACKAGE_SEARCHABLE_FIELDS}
            fieldLabels={{
              displayName: "name",
              packageName: "name",
              packageId: "ID",
            }}
          />
        </div>

        <div className={viewStyles.panelBody}>
          <PackageBoxGrid
            packages={paginatedPackages}
            emptyMessage={
              displayPackages.length
                ? "No packages match your search."
                : viewMode
                  ? "No applicable packages found."
                  : "No packages selected."
            }
          />
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={filteredPackages.length}
          pageSize={PACKAGE_SELECTOR_PAGE_SIZE}
          onPageChange={setCurrentPage}
          itemLabel="packages"
        />
      </div>

      {viewMode ? null : (
        <div className={styles.infoBanner}>
          <Icon name="info" fontSize="small" />
          <span>Coupon is applicable only to the selected packages above.</span>
        </div>
      )}
    </div>
  );
};

const PackageBoxGrid = ({ packages, emptyMessage }) => {
  if (packages.length === 0) {
    return <EmptyState message={emptyMessage} className={viewStyles.emptyState} />;
  }

  return (
    <div className={viewStyles.boxGrid}>
      {packages.map((pkg) => (
        <div key={pkg.packageId} className={viewStyles.packageBox}>
          <span className={viewStyles.packageBoxName}>{getPackageLabel(pkg)}</span>
          <span className={viewStyles.packageBoxId}>ID: {pkg.packageId}</span>
        </div>
      ))}
    </div>
  );
};

export default PackageListView;
